import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Plus, CheckCircle, MessageCircle, ClipboardList, Calendar,
  Search, Send, Paperclip, Download, User, Building2, X, Check, Menu
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRMTasks = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Create task dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    task_type: 'task',
    customer_id: '',
    title: '',
    description: '',
    due_date: '',
    assigned_to: []
  });
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // View task dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [newResponse, setNewResponse] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchTasks();
    fetchTeamMembers();
    fetchCustomers();
  }, [statusFilter, typeFilter, dateFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/crm/tasks?user_id=${user.user_id}&status=${statusFilter}&task_type=${typeFilter}`
      );
      setTasks(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members?user_id=${user.user_id}`);
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/crm/customers?user_id=${user.user_id}`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return false;
    const query = customerSearch.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(query) ||
      c.tax_id?.includes(query)
    );
  }).slice(0, 10);

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setTaskForm(prev => ({ ...prev, customer_id: customer.customer_id }));
    setCustomerSearch(customer.company_name);
    setShowCustomerSuggestions(false);
  };

  const toggleAssignee = (userId) => {
    setTaskForm(prev => {
      const current = prev.assigned_to || [];
      if (current.includes(userId)) {
        return { ...prev, assigned_to: current.filter(id => id !== userId) };
      } else {
        return { ...prev, assigned_to: [...current, userId] };
      }
    });
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    if (!taskForm.customer_id) {
      toast.error('Επιλέξτε πελάτη');
      return;
    }
    if (!taskForm.title.trim()) {
      toast.error('Ο τίτλος είναι υποχρεωτικός');
      return;
    }
    if (!taskForm.due_date) {
      toast.error('Η ημερομηνία είναι υποχρεωτική');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        user_id: user.user_id,
        task_type: taskForm.task_type,
        customer_id: taskForm.customer_id,
        title: taskForm.title,
        description: taskForm.description || '',
        due_date: taskForm.due_date
      });
      
      // Add assigned_to as multiple params
      taskForm.assigned_to.forEach(uid => {
        params.append('assigned_to', uid);
      });
      
      await axios.post(`${API}/crm/tasks?${params.toString()}`);
      toast.success(taskForm.task_type === 'task' ? 'Το καθήκον δημιουργήθηκε' : 'Η επικοινωνία δημιουργήθηκε');
      setCreateDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας');
    }
  };

  const resetForm = () => {
    setTaskForm({
      task_type: 'task',
      customer_id: '',
      title: '',
      description: '',
      due_date: '',
      assigned_to: []
    });
    setSelectedCustomer(null);
    setCustomerSearch('');
  };

  const openViewDialog = async (task) => {
    try {
      const response = await axios.get(`${API}/crm/tasks/${task.task_id}?user_id=${user.user_id}`);
      setViewingTask(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    }
  };

  const handleAddResponse = async () => {
    if (!newResponse.trim()) return;
    
    try {
      await axios.post(
        `${API}/crm/tasks/${viewingTask.task_id}/response?user_id=${user.user_id}&message=${encodeURIComponent(newResponse)}`
      );
      toast.success('Η απάντηση προστέθηκε');
      setNewResponse('');
      // Refresh task
      const response = await axios.get(`${API}/crm/tasks/${viewingTask.task_id}?user_id=${user.user_id}`);
      setViewingTask(response.data);
    } catch (error) {
      toast.error('Σφάλμα');
    }
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/crm/tasks/${viewingTask.task_id}/attachment?user_id=${user.user_id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Το αρχείο ανέβηκε');
      // Refresh task
      const response = await axios.get(`${API}/crm/tasks/${viewingTask.task_id}?user_id=${user.user_id}`);
      setViewingTask(response.data);
    } catch (error) {
      toast.error('Σφάλμα ανεβάσματος');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${API}/crm/tasks/${viewingTask.task_id}/attachment/${attachment.attachment_id}/download?user_id=${user.user_id}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Σφάλμα λήψης');
    }
  };

  const handleCompleteTask = async () => {
    try {
      await axios.put(`${API}/crm/tasks/${viewingTask.task_id}/complete?user_id=${user.user_id}`);
      toast.success('Ολοκληρώθηκε');
      setViewDialogOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('el-GR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('el-GR');
    } catch {
      return dateStr;
    }
  };

  // Separate tasks by type and apply date filter
  const filterByDate = (items) => {
    if (!dateFilter) return items;
    return items.filter(t => t.due_date && t.due_date.startsWith(dateFilter));
  };
  
  const taskItems = filterByDate(tasks.filter(t => t.task_type === 'task'));
  const communicationItems = filterByDate(tasks.filter(t => t.task_type === 'communication'));

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed top-4 left-4 z-30 p-2 bg-white rounded-lg shadow-md border border-slate-200"
        data-testid="mobile-menu-button"
      >
        <Menu className="h-6 w-6 text-slate-700" />
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-200 md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button
          onClick={() => setSidebarOpen(false)}
          className="md:hidden absolute top-4 right-4 p-1 text-slate-500 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="p-4 border-b border-slate-200">
          <button
            onClick={() => navigate('/crm')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Πίσω στο CRM
          </button>
          <h1 className="text-xl font-bold text-slate-800">Καθήκοντα & Επικοινωνίες</h1>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <button
                onClick={() => { navigate('/crm'); setSidebarOpen(false); }}
                className="w-full px-4 py-2 rounded-lg text-left text-slate-600 hover:bg-slate-50"
              >
                Πελατολόγιο
              </button>
            </li>
            <li>
              <button
                onClick={() => { navigate('/crm/appointments'); setSidebarOpen(false); }}
                className="w-full px-4 py-2 rounded-lg text-left text-slate-600 hover:bg-slate-50"
              >
                Πωλήσεις - Ραντεβού
              </button>
            </li>
            <li>
              <button
                className="w-full px-4 py-2 rounded-lg text-left bg-purple-50 text-purple-700 font-medium"
              >
                Καθήκοντα & Επικοινωνίες
              </button>
            </li>
            <li>
              <button
                onClick={() => { navigate('/crm/settings'); setSidebarOpen(false); }}
                className="w-full px-4 py-2 rounded-lg text-left text-slate-600 hover:bg-slate-50"
              >
                Ρυθμίσεις
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 md:p-6 pt-16 md:pt-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Ανοιχτά</SelectItem>
                  <SelectItem value="completed">Ολοκληρωμένα</SelectItem>
                  <SelectItem value="all">Όλα</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-500 hidden sm:block" />
                <Input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-36"
                  data-testid="date-filter"
                />
                {dateFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDateFilter('')}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-task-btn" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Νέο
            </Button>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tasks Column */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-blue-600" />
                Καθήκοντα
                <span className="text-sm font-normal text-slate-500">({taskItems.length})</span>
              </h3>
              
              {taskItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Δεν υπάρχουν καθήκοντα
                </div>
              ) : (
                <div className="space-y-3">
                  {taskItems.map(task => (
                    <div
                      key={task.task_id}
                      onClick={() => openViewDialog(task)}
                      className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                        task.status === 'completed' ? 'bg-slate-50 border-slate-200' : 'bg-white border-blue-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                            {task.title}
                          </h4>
                          <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                            <Building2 className="h-3 w-3" />
                            {task.customer_name}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(task.due_date)}
                          </p>
                        </div>
                        {task.status === 'completed' && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                      {task.assigned_to_names?.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {task.assigned_to_names.map((name, i) => (
                            <span key={i} className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Communications Column */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-purple-600" />
                Επικοινωνίες
                <span className="text-sm font-normal text-slate-500">({communicationItems.length})</span>
              </h3>
              
              {communicationItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Δεν υπάρχουν επικοινωνίες
                </div>
              ) : (
                <div className="space-y-3">
                  {communicationItems.map(task => (
                    <div
                      key={task.task_id}
                      onClick={() => openViewDialog(task)}
                      className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                        task.status === 'completed' ? 'bg-slate-50 border-slate-200' : 'bg-white border-purple-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`font-medium ${task.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                            {task.title}
                          </h4>
                          <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                            <Building2 className="h-3 w-3" />
                            {task.customer_name}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(task.due_date)}
                          </p>
                        </div>
                        {task.status === 'completed' && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                      {task.assigned_to_names?.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {task.assigned_to_names.map((name, i) => (
                            <span key={i} className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Νέο Καθήκον / Επικοινωνία</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label>Τύπος</Label>
              <Select
                value={taskForm.task_type}
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, task_type: v }))}
              >
                <SelectTrigger data-testid="task-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="task">Καθήκον για πελάτη</SelectItem>
                  <SelectItem value="communication">Επικοινωνία για πελάτη</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="relative">
              <Label>Πελάτης (Επωνυμία ή ΑΦΜ)</Label>
              <div className="flex gap-2">
                <Input
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerSuggestions(e.target.value.length > 0);
                  }}
                  onFocus={() => customerSearch && setShowCustomerSuggestions(true)}
                  placeholder="Πληκτρολογήστε για αναζήτηση..."
                  data-testid="customer-search-input"
                />
                {selectedCustomer && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setTaskForm(prev => ({ ...prev, customer_id: '' }));
                      setCustomerSearch('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {showCustomerSuggestions && filteredCustomers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredCustomers.map(customer => (
                    <button
                      key={customer.customer_id}
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-slate-100 flex justify-between"
                      onClick={() => selectCustomer(customer)}
                    >
                      <span>{customer.company_name}</span>
                      {customer.tax_id && (
                        <span className="text-xs text-slate-500">{customer.tax_id}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Τίτλος *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="π.χ. Αποστολή προσφοράς"
                required
                data-testid="task-title-input"
              />
            </div>

            <div>
              <Label>Περιγραφή</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="task-description-input"
              />
            </div>

            <div>
              <Label>Ημερομηνία Εργασίας *</Label>
              <Input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                required
                data-testid="task-due-date-input"
              />
            </div>

            {teamMembers.length > 0 && (
              <div>
                <Label className="mb-2 block">Μέλη Ομάδας</Label>
                <div className="flex flex-wrap gap-2">
                  {teamMembers.map(member => (
                    <button
                      key={member.user_id}
                      type="button"
                      onClick={() => toggleAssignee(member.user_id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        taskForm.assigned_to.includes(member.user_id)
                          ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                          : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
                      }`}
                    >
                      {member.role_name || member.email}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
                Ακύρωση
              </Button>
              <Button type="submit" data-testid="save-task-btn">
                Αποθήκευση
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Task Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingTask && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {viewingTask.task_type === 'task' ? (
                    <ClipboardList className="h-5 w-5 text-blue-600" />
                  ) : (
                    <MessageCircle className="h-5 w-5 text-purple-600" />
                  )}
                  {viewingTask.title}
                  {viewingTask.status === 'completed' && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-2">
                      Ολοκληρωμένο
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    <span className="font-medium">{viewingTask.customer_name}</span>
                    {viewingTask.customer_afm && (
                      <span className="text-slate-500">({viewingTask.customer_afm})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="h-4 w-4" />
                    Ημ. Εργασίας: {formatDate(viewingTask.due_date)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <User className="h-4 w-4" />
                    Δημιουργήθηκε από: {viewingTask.created_by_name}
                  </div>
                  {viewingTask.description && (
                    <p className="text-sm text-slate-700 mt-2">{viewingTask.description}</p>
                  )}
                </div>

                {/* Assigned */}
                {viewingTask.assigned_to_names?.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500">Ανατέθηκε σε:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {viewingTask.assigned_to_names.map((name, i) => (
                        <span key={i} className="text-sm bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Attachments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-slate-500">Συνημμένα ({viewingTask.attachments?.length || 0})</Label>
                    {viewingTask.status !== 'completed' && (
                      <label className="cursor-pointer">
                        <Button variant="ghost" size="sm" asChild disabled={uploading}>
                          <span>
                            <Paperclip className="h-4 w-4 mr-1" />
                            {uploading ? 'Ανέβασμα...' : 'Προσθήκη'}
                          </span>
                        </Button>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleUploadAttachment}
                        />
                      </label>
                    )}
                  </div>
                  {viewingTask.attachments?.length > 0 && (
                    <div className="space-y-1">
                      {viewingTask.attachments.map(att => (
                        <div
                          key={att.attachment_id}
                          className="flex items-center justify-between p-2 bg-slate-50 rounded"
                        >
                          <span className="text-sm text-slate-700">{att.filename}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadAttachment(att)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Responses */}
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">
                    Απαντήσεις ({viewingTask.responses?.length || 0})
                  </Label>
                  
                  {viewingTask.responses?.length > 0 && (
                    <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                      {viewingTask.responses.map(resp => (
                        <div key={resp.response_id} className="bg-slate-50 rounded p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">{resp.user_name}</span>
                            <span className="text-xs text-slate-500">{formatDateTime(resp.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600">{resp.message}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {viewingTask.status !== 'completed' && (
                    <div className="flex gap-2">
                      <Input
                        value={newResponse}
                        onChange={(e) => setNewResponse(e.target.value)}
                        placeholder="Γράψτε απάντηση..."
                        onKeyPress={(e) => e.key === 'Enter' && handleAddResponse()}
                      />
                      <Button onClick={handleAddResponse} disabled={!newResponse.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {viewingTask.status !== 'completed' && viewingTask.created_by === user.user_id && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={handleCompleteTask}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Ολοκληρώθηκε
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMTasks;
